import { toast } from '@wellesley/system-toast'
import { useState } from 'react'
import CommonDialog from 'components/modals/CommonDialog'
import { ButtonLoader } from '@wellesley/ui'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import { Relationship, User, UserFull } from '@wellesley/openapi'
import {
  isMuted,
  useDeleteApiV1MuteByUserIdMutation,
  usePostApiV1MuteByUserIdMutation,
} from '@wellesley/user'
import MuteOptions, { muteDuration } from './MuteOptions'

import { useSwitchState } from '../useSwitchState'

type HookProps = {
  user?: User | UserFull
  relationship: Relationship
  showConfirmationDialog?: boolean
  onMute?: () => void
  onUnmute?: () => void
}

export type DurationKeys = keyof typeof muteDuration

const useMute = ({
  user,
  relationship,
  showConfirmationDialog = true,
  onMute,
  onUnmute,
}: HookProps) => {
  const [mute, { isLoading: isMuteLoading }] =
    usePostApiV1MuteByUserIdMutation()

  const [unmute, { isLoading: isUnMuteLoading }] =
    useDeleteApiV1MuteByUserIdMutation()

  const [duration, setDuration] = useState<DurationKeys>('forever')

  const username = user ? getUserNameWithDomain(user) : ''

  const [isMuteDialogOpened, openDialog, closeDialog] = useSwitchState()

  const isUserMuted = isMuted(relationship)

  const handleMute = (muteProp?: number) => {
    if (!user) return

    const muteTime =
      muteProp || muteDuration[duration as keyof typeof muteDuration]

    void mute({
      userId: user.id,
      ...(muteTime && {
        duration: muteTime,
      }),
    })
    toast.success(`${username} is muted`, {
      iconVariant: 'bell-off',
      dataTestId: 'toast-mute-success',
    })
    closeDialog()
    onMute?.()
  }

  const handleUnmute = () => {
    if (!user) return

    void unmute({ userId: user.id })
    toast.success(`${username} is unmuted`, {
      iconVariant: 'bell',
      dataTestId: 'toast-unmute-success',
    })
    onUnmute?.()
  }

  const isLoading = isMuteLoading || isUnMuteLoading

  const renderMuteDialog = () => {
    return user ? (
      <CommonDialog
        open={isMuteDialogOpened}
        onCloseModal={closeDialog}
        title={`Do you want to mute ${user.username}?`}
        className="p-none"
        data-testid="mute-dialog"
      >
        <span className="text-sm text-gray-500">
          Muted users can still follow you and see your content, but you will
          not see any content from them. They will not know that they have been
          muted.
        </span>
        <div className="gap-xxxs mt-m flex">
          <MuteOptions selected={duration} onSelect={setDuration} />
        </div>
        <ButtonLoader
          variant="primary"
          onClick={() => void handleMute()}
          isLoading={isMuteLoading}
          className="mt-m ml-auto block"
          data-testid="submitAction"
        >
          Mute
        </ButtonLoader>
      </CommonDialog>
    ) : null
  }

  return {
    isLoading,
    isMuted: isUserMuted,
    mute: showConfirmationDialog ? openDialog : handleMute,
    unmute: handleUnmute,
    renderMuteDialog,
    isMuteDialogOpened,
  }
}

export default useMute
